/**
 * Trim Calculator Component
 *
 * Calculates additional J-Trim needed for window and door openings.
 * J-Trim comes in 10' lengths, so we calculate perimeters and divide.
 */

import { useTranslation } from 'react-i18next';
import React, { useState, useMemo, useEffect } from 'react';
import { useEstimator } from '../../context/EstimatorContext';
import { trackTrimItemAdded } from '../../utils/gtmTracking';
import { DimensionInput, UnitToggle } from './DimensionInput';
import { useUnitMode } from '../../hooks/useUnitMode';
import {
  DoorOpen,
  Square,
  Plus,
  Trash2,
  Calculator,
  Info,
  AlertCircle,
} from 'lucide-react';
import './TrimCalculator.css';

export interface Opening {
  id: string;
  type: 'window' | 'door';
  width: number;
  height: number;
  name: string;
}

interface TrimCalculatorProps {
  onTrimCalculated?: (trimPieces: number, perimeter: number) => void;
  onOpeningsChange?: (openings: Opening[]) => void;
}

// Common presets for windows and doors
// `id` keys both the i18n label and the default opening name. `name` is the
// English text, kept because applying a preset seeds the opening's name and
// that name is stored on the estimate and printed in the PDF — a stored value
// must not change when the viewer switches language.
const WINDOW_PRESETS = [
  { id: 'small', name: 'Small', width: 2, height: 3 },
  { id: 'standard', name: 'Standard', width: 3, height: 4 },
  { id: 'large', name: 'Large', width: 4, height: 5 },
  { id: 'picture', name: 'Picture', width: 5, height: 4 },
];

const DOOR_PRESETS = [
  { id: 'interior', name: 'Interior', width: 2.5, height: 6.67 },
  { id: 'entry', name: 'Entry', width: 3, height: 6.67 },
  { id: 'wideEntry', name: 'Wide Entry', width: 3.5, height: 6.67 },
  { id: 'double', name: 'Double', width: 6, height: 6.67 },
];

const J_TRIM_LENGTH = 10; // feet

export const TrimCalculator: React.FC<TrimCalculatorProps> = ({ onTrimCalculated, onOpeningsChange }) => {
  const { t } = useTranslation('results');
  const { state } = useEstimator();
  const [openings, setOpenings] = useState<Opening[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  // One ft/in <-> decimal toggle for the opening width/height group.
  const [unitMode, setUnitMode] = useUnitMode('trim-opening');
  const [newOpening, setNewOpening] = useState<Partial<Opening>>({
    type: 'window',
    width: 3,
    height: 4,
    name: '',
  });

  // Calculate total perimeter and trim needed
  const { totalPerimeter, trimPiecesNeeded } = useMemo(() => {
    const perimeter = openings.reduce((sum, opening) => {
      return sum + 2 * (opening.width + opening.height);
    }, 0);

    const pieces = Math.ceil(perimeter / J_TRIM_LENGTH);

    // Notify parent if callback provided
    if (onTrimCalculated) {
      onTrimCalculated(pieces, perimeter);
    }

    return { totalPerimeter: perimeter, trimPiecesNeeded: pieces };
  }, [openings, onTrimCalculated]);

  // Notify parent when openings change
  useEffect(() => {
    if (onOpeningsChange) {
      onOpeningsChange(openings);
    }
  }, [openings, onOpeningsChange]);

  const addOpening = () => {
    if (!newOpening.type || !newOpening.width || !newOpening.height) return;

    const opening: Opening = {
      id: `${Date.now()}`,
      type: newOpening.type as 'window' | 'door',
      width: newOpening.width,
      height: newOpening.height,
      name: newOpening.name || `${t(newOpening.type === 'window' ? 'pdf.trim.window' : 'pdf.trim.door')} ${openings.length + 1}`,
    };

    setOpenings([...openings, opening]);
    trackTrimItemAdded(state.calculationMode, opening.type, opening.width, opening.height);
    setNewOpening({
      type: 'window',
      width: 3,
      height: 4,
      name: '',
    });
    setShowAddForm(false);
  };

  const removeOpening = (id: string) => {
    setOpenings(openings.filter(o => o.id !== id));
  };

  const applyPreset = (preset: { width: number; height: number; name: string; id: string }) => {
    setNewOpening({
      ...newOpening,
      width: preset.width,
      height: preset.height,
      name: preset.name,
    });
  };

  return (
    <div className="trim-calculator">
      <div className="trim-calculator-header">
        <h3 className="trim-calculator-title">
          <Calculator size={20} className="trim-title-icon" />
          {t('trim.title')}
        </h3>
        <p className="trim-calculator-description">{t('trim.description')}</p>
      </div>

      {/* Opening List */}
      {openings.length > 0 && (
        <div className="openings-list">
          {openings.map(opening => (
            <div key={opening.id} className="opening-item">
              <div className="opening-icon">
                {opening.type === 'window' ? (
                  <Square size={18} />
                ) : (
                  <DoorOpen size={18} />
                )}
              </div>
              <div className="opening-details">
                <span className="opening-name">{opening.name}</span>
                <span className="opening-size">
                  {opening.width}' × {opening.height}' = {(2 * (opening.width + opening.height)).toFixed(1)}' perimeter
                </span>
              </div>
              <button
                type="button"
                className="opening-remove"
                onClick={() => removeOpening(opening.id)}
                aria-label={t('trim.removeOpening')}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Opening Form */}
      {showAddForm ? (
        <div className="add-opening-form">
          {/* Type Selection */}
          <div className="form-row">
            <label className="form-label">{t('pdf.trim.colType')}</label>
            <div className="type-selector">
              <button
                type="button"
                className={`type-option ${newOpening.type === 'window' ? 'selected' : ''}`}
                onClick={() => setNewOpening({ ...newOpening, type: 'window' })}
              >
                <Square size={16} />
                {t('pdf.trim.window')}
              </button>
              <button
                type="button"
                className={`type-option ${newOpening.type === 'door' ? 'selected' : ''}`}
                onClick={() => setNewOpening({ ...newOpening, type: 'door' })}
              >
                <DoorOpen size={16} />
                {t('pdf.trim.door')}
              </button>
            </div>
          </div>

          {/* Presets */}
          <div className="form-row">
            <label className="form-label">{t('trim.quickPresets')}</label>
            <div className="preset-chips">
              {(newOpening.type === 'window' ? WINDOW_PRESETS : DOOR_PRESETS).map(preset => (
                <button
                  key={preset.id}
                  type="button"
                  className="preset-chip"
                  onClick={() => applyPreset(preset)}
                >
                  {t('trim.presetChip', {
                    name: t(`trim.presets.${preset.id}`),
                    width: preset.width,
                    height: preset.height,
                  })}
                </button>
              ))}
            </div>
          </div>

          {/* Dimensions */}
          <div className="dim-group form-row">
            <div className="dimensions-row">
            <div className="dimension-field">
              <label className="form-label">{t('trim.width')}</label>
              <DimensionInput
                value={newOpening.width ?? 0}
                onChange={v => setNewOpening({ ...newOpening, width: v })}
                mode={unitMode}
                min={0.5}
                max={20}
                hasError={(newOpening.width || 0) < 0.5 || (newOpening.width || 0) > 20}
                ariaLabel={t('trim.openingWidth')}
              />
              {((newOpening.width || 0) < 0.5 || (newOpening.width || 0) > 20) && (
                <span className="field-hint error">0.5-20 ft</span>
              )}
            </div>
            <span className="dimension-x">×</span>
            <div className="dimension-field">
              <label className="form-label">{t('config.height')}</label>
              <DimensionInput
                value={newOpening.height ?? 0}
                onChange={v => setNewOpening({ ...newOpening, height: v })}
                mode={unitMode}
                min={0.5}
                max={12}
                hasError={(newOpening.height || 0) < 0.5 || (newOpening.height || 0) > 12}
                ariaLabel={t('trim.openingHeight')}
              />
              {((newOpening.height || 0) < 0.5 || (newOpening.height || 0) > 12) && (
                <span className="field-hint error">0.5-12 ft</span>
              )}
            </div>
            </div>
            <UnitToggle mode={unitMode} onChange={setUnitMode} />
          </div>

          {/* Name (optional) */}
          <div className="form-row">
            <label className="form-label">{t('trim.nameOptional')}</label>
            <input
              type="text"
              className="name-input"
              value={newOpening.name}
              placeholder={`${t(newOpening.type === 'window' ? 'pdf.trim.window' : 'pdf.trim.door')} ${openings.length + 1}`}
              onChange={e => setNewOpening({ ...newOpening, name: e.target.value })}
            />
          </div>

          {/* Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setShowAddForm(false)}
            >
              {t('auth:cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={addOpening}
            >
              {t('trim.addOpening')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="add-opening-button"
          onClick={() => setShowAddForm(true)}
        >
          <Plus size={18} />
          {t('trim.addWindowOrDoor')}
        </button>
      )}

      {/* Results */}
      {openings.length > 0 && (
        <div className="trim-results">
          <div className="result-item">
            <span className="result-label">{t('trim.totalPerimeter')}</span>
            <span className="result-value">{t('config.feet', { value: totalPerimeter.toFixed(1) })}</span>
          </div>
          <div className="result-item highlight">
            <span className="result-label">{t('trim.jTrimNeeded')}</span>
            <span className="result-value">{t('trim.pieces', { count: trimPiecesNeeded })}</span>
          </div>
          <p className="result-note">
            <Info size={14} />
            {t('trim.note')}
          </p>
        </div>
      )}

      {/* Empty State */}
      {openings.length === 0 && !showAddForm && (
        <div className="empty-state">
          <AlertCircle size={24} />
          <p>{t('trim.empty')}</p>
        </div>
      )}
    </div>
  );
};

export default TrimCalculator;
